import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { OK_GREEN, OK_GREEN_TEXT } from "../designTokens";

// Short display names for the 5 steps — used wherever space is tight (the hub's hero
// summary line, the history list's compact per-day row).
export const SHORT_NAMES = { vehicle: "Vehicle", tools: "Tools", attendance: "Attendance", toolbox: "Toolbox", devices: "Devices" };

// Icon-chip colors are fixed per step category in the source design (hs.html and
// index.html both: .workflow-icon.green/.blue/.purple/.red), NOT derived from
// done/pending state — Tools stays blue even when done, Devices stays red even before
// any alert exists. Kept as a lookup here rather than duplicating hex values per step.
const ICON_TONES = {
  vehicle: { iconBg: "rgba(34,197,94,0.12)", iconColor: "#4ADE80" },
  tools: { iconBg: "rgba(59,130,246,0.12)", iconColor: "#93C5FD" },
  attendance: { iconBg: "rgba(253,185,64,0.12)", iconColor: AMBER },
  toolbox: { iconBg: "rgba(168,85,247,0.12)", iconColor: "#C4B5FD" },
  devices: { iconBg: "rgba(239,68,68,0.12)", iconColor: "#F87171" },
};

// Status-pill tone → {bg, color}, matching the source's .ws-done/.ws-progress/
// .ws-pending/.ws-alert exactly. Separate from the icon-chip color above — the pill
// tracks state, the icon-chip tracks category.
const TONE_STYLES = {
  done: { bg: "rgba(34,197,94,0.12)", color: OK_GREEN_TEXT },
  progress: { bg: "rgba(253,185,64,0.14)", color: AMBER },
  pending: { bg: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" },
  alert: { bg: "rgba(239,68,68,0.12)", color: "#F87171" },
};

// Shared between the H&S hub and the Home dashboard — both show the identical 5-step
// H&S progress summary, computed from the same hs/day/ aggregate.
export function computeStepMeta(day) {
  const toolsDone = day.tools_total > 0 && day.tools_done === day.tools_total;
  const attendanceDone = day.attendance_total > 0 && day.attendance_verified === day.attendance_total;
  const toolboxDone = day.toolbox_topic_set && day.toolbox_ack_total > 0 && day.toolbox_ack_done === day.toolbox_ack_total;
  const devicesStarted = day.devices_checked > 0;
  const devicesAlerted = day.devices_alerts > 0;
  const devicesDone = devicesStarted && !devicesAlerted;

  return [
    {
      key: "vehicle",
      name: "Vehicle inspection",
      path: "/field-admin/hs/vehicle/",
      Icon: DirectionsCarFilledOutlinedIcon,
      ...ICON_TONES.vehicle,
      done: day.vehicle_complete,
      tone: day.vehicle_complete ? "done" : "pending",
      statusLabel: day.vehicle_complete ? "Done" : "Pending",
      sub: day.vehicle_complete ? "Checklist signed off" : "Not started yet",
      progressPct: day.vehicle_complete ? 100 : 0,
    },
    {
      key: "tools",
      name: "Tool inspection",
      path: "/field-admin/hs/tools/",
      Icon: BuildOutlinedIcon,
      ...ICON_TONES.tools,
      done: toolsDone,
      tone: toolsDone ? "done" : day.tools_total === 0 ? "pending" : "progress",
      statusLabel: day.tools_total === 0 ? "Pending" : `${day.tools_done} / ${day.tools_total}`,
      sub: day.tools_total === 0 ? "Not started yet" : `${day.tools_done} of ${day.tools_total} installers checked`,
      progressPct: day.tools_total === 0 ? 0 : Math.round((day.tools_done / day.tools_total) * 100),
    },
    {
      key: "attendance",
      name: "Attendance register",
      path: "/field-admin/hs/attendance/",
      Icon: PeopleOutlineIcon,
      ...ICON_TONES.attendance,
      done: attendanceDone,
      tone: attendanceDone ? "done" : day.attendance_total === 0 ? "pending" : "progress",
      statusLabel: day.attendance_total === 0 ? "Pending" : `${day.attendance_verified} / ${day.attendance_total}`,
      sub: day.attendance_total === 0 ? "Not started yet" : `${day.attendance_verified} of ${day.attendance_total} verified`,
      progressPct: day.attendance_total === 0 ? 0 : Math.round((day.attendance_verified / day.attendance_total) * 100),
    },
    {
      key: "toolbox",
      name: "Toolbox talk",
      path: "/field-admin/hs/toolbox/",
      Icon: AssignmentTurnedInOutlinedIcon,
      ...ICON_TONES.toolbox,
      done: toolboxDone,
      tone: toolboxDone ? "done" : !day.toolbox_topic_set ? "pending" : "progress",
      statusLabel: !day.toolbox_topic_set ? "Pending" : `${day.toolbox_ack_done} / ${day.toolbox_ack_total}`,
      sub: !day.toolbox_topic_set ? "No topic set yet" : `${day.toolbox_ack_done} of ${day.toolbox_ack_total} acknowledged`,
      progressPct: !day.toolbox_topic_set || day.toolbox_ack_total === 0 ? 0 : Math.round((day.toolbox_ack_done / day.toolbox_ack_total) * 100),
    },
    {
      key: "devices",
      name: "Device & data status",
      path: "/field-admin/hs/devices/",
      Icon: PhoneAndroidOutlinedIcon,
      ...ICON_TONES.devices,
      done: devicesDone,
      tone: devicesAlerted ? "alert" : devicesDone ? "done" : "pending",
      statusLabel: devicesAlerted ? `${day.devices_alerts} alert${day.devices_alerts === 1 ? "" : "s"}` : devicesStarted ? "Recorded" : "Pending",
      sub: devicesAlerted
        ? `${day.devices_alerts} of ${day.devices_checked} need a top-up`
        : devicesStarted
        ? `${day.devices_checked} device${day.devices_checked === 1 ? "" : "s"} recorded`
        : "Not started yet",
      progressPct: devicesStarted ? Math.round(((day.devices_checked - day.devices_alerts) / day.devices_checked) * 100) : 0,
    },
  ];
}

// columns=1 (default) is the H&S hub's own layout — always 1-column, matching hs.html's
// .workflow-stack exactly. Home passes columns={2} — index.html's version is genuinely
// 2-up above phone width; the two source designs were never meant to share a stack shape,
// only the tile skin.
// `locked` is the day-session state, not a step state — every tile takes the same
// treatment at once, which is why it is a prop here rather than a sixth entry in
// TONE_STYLES. Tiles stay navigable: a signed-off day must still be readable, and the
// backend is what refuses the writes (DayLocked -> 409).
export function WorkflowTileList({ steps, columns = 1, showStepNumbers = false, locked = false }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: columns === 2 ? { xs: "1fr", sm: "1fr 1fr" } : "1fr", gap: "10px" }}>
      {steps.map((step, index) => {
        const toneStyle = TONE_STYLES[step.tone] || TONE_STYLES.pending;
        return (
          <Box
            key={step.key}
            onClick={() => navigate(step.path)}
            sx={{
              backgroundColor: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderLeft: `2px solid ${locked ? "rgba(255,255,255,0.14)" : AMBER}`,
              borderRadius: "12px",
              px: "16px",
              py: "14px",
              display: "flex",
              alignItems: "center",
              gap: "13px",
              cursor: "pointer",
              opacity: locked ? 0.7 : 1,
              transition: "border-color 0.2s, transform 0.15s",
              // No lift on a locked day: the tile still opens, but it should not invite
              // the FA to act on it.
              "&:hover": locked
                ? { borderColor: "rgba(255,255,255,0.16)" }
                : { borderColor: "rgba(253,185,64,0.35)", borderLeftColor: AMBER, transform: "translateY(-1px)" },
            }}
          >
            {showStepNumbers ? (
              <Box
                sx={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  backgroundColor: step.done ? "rgba(34,197,94,0.18)" : "rgba(253,185,64,0.18)",
                  color: step.done ? OK_GREEN_TEXT : AMBER,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {step.done ? <CheckIcon sx={{ fontSize: "0.95rem" }} /> : index + 1}
              </Box>
            ) : null}
            <Box sx={{ width: "38px", height: "38px", borderRadius: "9px", backgroundColor: step.iconBg, color: step.iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <step.Icon sx={{ fontSize: "1.2rem" }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <Typography sx={{ color: "#fff", fontSize: "0.95rem", fontWeight: 600 }}>{step.name}</Typography>
                <Box
                  sx={{
                    fontSize: "0.66rem",
                    fontWeight: 700,
                    px: "7px",
                    py: "3px",
                    borderRadius: "4px",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    flexShrink: 0,
                    backgroundColor: toneStyle.bg,
                    color: toneStyle.color,
                  }}
                >
                  {step.statusLabel}
                </Box>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.74rem", lineHeight: 1.35, mt: "2px" }}>{step.sub}</Typography>
              <Box sx={{ mt: "8px", height: "3px", backgroundColor: "rgba(255,255,255,0.08)", borderRadius: "2px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${step.progressPct}%`, backgroundColor: step.done ? OK_GREEN : AMBER, borderRadius: "2px" }} />
              </Box>
            </Box>
            <ChevronRightIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: "1.1rem", flexShrink: 0 }} />
          </Box>
        );
      })}
    </Box>
  );
}
